/**
 * When true, the data owner column shows `Branch name: email` per branch (parent entity / branch).
 * When false, only email addresses are shown (branch prefix is still computed below for future use).
 */
export const DATA_OWNER_COLUMN_INCLUDE_BRANCH_LABELS = true;

/** Data-owner emails from all branches (same logic as Configure Permissions). */
function getDataOwnerEmailsForGroup(group) {
	const emails = [];
	group?.branches?.forEach((b) => {
		b.emails?.forEach((e) => {
			if (e) emails.push(e);
		});
	});
	return emails;
}

/** Reviewers aggregated by approval level across branches. */
function getReviewersGroupedByLevel(group) {
	const levelMap = new Map();
	group?.branches?.forEach((b) => {
		b.reviewers?.forEach((r) => {
			const lvl = r.level ?? 1;
			if (!levelMap.has(lvl)) levelMap.set(lvl, new Set());
			// GET /assessments/:id?fetchAll=true returns email/name; older payloads used userEmail/userName
			const ident = (r.email || r.userEmail || r.name || r.userName || '').trim();
			if (ident) levelMap.get(lvl).add(ident);
		});
	});
	return [...levelMap.entries()]
		.sort((a, b) => a[0] - b[0])
		.map(([level, set]) => ({ level, emails: [...set] }));
}

/**
 * Walks form skeleton (sections → subSections → fields) and yields one entry per question field.
 * @param {object[]} sections - Top-level sections from form preview API
 * @returns {Array<{ fieldId: number, questionName: string, ancestorIds: number[], sectionTitle: string, subSectionTitles: string[] }>}
 */
export function collectQuestionRowsFromSkeleton(sections) {
	const rows = [];

	const walk = (node, chainIds, chainTitles) => {
		if (!node) return;
		const ids = [...chainIds, node.id];
		const titles = [...chainTitles, node.title];
		for (const field of node.fields || []) {
			rows.push({
				fieldId: field.id,
				questionName: field.name || '',
				ancestorIds: ids,
				sectionTitle: titles[0] || '',
				subSectionTitles: titles.slice(1)
			});
		}
		for (const sub of node.subSections || []) {
			walk(sub, ids, titles);
		}
	};

	for (const sec of sections || []) {
		walk(sec, [], []);
	}

	return rows;
}

/**
 * Permission applies if it targets the whole form (no section) or its section id is on the ancestor chain.
 */
export function permissionAppliesToField(perm, ancestorIds) {
	if (!perm) return false;
	if (perm.section == null || perm.section === undefined) return true;
	const sid = perm.section.id;
	return ancestorIds.includes(sid);
}

/**
 * @returns {{ groups: object[], stakeholders: object[] }}
 */
export function getApplicablePermissionTargets(formPermissions, ancestorIds) {
	const groups = [];
	const stakeholders = [];
	const seenGroup = new Set();
	const seenStakeholder = new Set();

	for (const perm of formPermissions || []) {
		if (!permissionAppliesToField(perm, ancestorIds)) continue;

		for (const g of perm.groups || []) {
			if (g?.id != null && !seenGroup.has(g.id)) {
				seenGroup.add(g.id);
				groups.push(g);
			}
		}
		for (const s of perm.stakeholders || []) {
			if (s?.id != null && !seenStakeholder.has(s.id)) {
				seenStakeholder.add(s.id);
				stakeholders.push(s);
			}
		}
	}

	return { groups, stakeholders };
}

/**
 * @param {object} group - Enterprise group from assessment permissions
 * @param {{ includeBranchLabels?: boolean }} [options] - defaults to {@link DATA_OWNER_COLUMN_INCLUDE_BRANCH_LABELS}
 */
export function formatOwnerLinesForGroup(group, options = {}) {
	const includeBranchLabels =
		options.includeBranchLabels ?? DATA_OWNER_COLUMN_INCLUDE_BRANCH_LABELS;
	const lines = [];
	const gname = group.name || group.groupName || '';
	group?.branches?.forEach((b) => {
		const branchLabel = (b.name || '').trim();
		const emails = (b.emails || []).filter(Boolean);
		if (emails.length > 0) {
			emails.forEach((e) => {
				if (includeBranchLabels && branchLabel) {
					lines.push(`${branchLabel}: ${e}`);
				} else {
					lines.push(e);
				}
			});
		} else if (branchLabel) {
			if (includeBranchLabels) {
				lines.push(branchLabel);
			}
		}
	});
	const emailFallback = getDataOwnerEmailsForGroup(group);
	if (lines.length === 0 && emailFallback.length > 0) {
		return emailFallback;
	}
	if (lines.length === 0 && gname) {
		lines.push(gname);
	}
	return lines;
}

/** Adds common branch / parent-entity string fields for search (API may use different keys). */
function addBranchEntitySearchTokens(branch, add) {
	if (!branch || typeof branch !== 'object') return;
	add(branch.name);
	add(branch.parentEntity);
	add(branch.parentName);
	add(branch.entityName);
	add(branch.label);
	add(branch.shortName);
	for (const [key, val] of Object.entries(branch)) {
		if (val == null || typeof val !== 'string') continue;
		if (['emails', 'reviewers'].includes(key)) continue;
		add(val);
	}
}

/**
 * Tokens used to filter rows by data owner search (emails, names, branch / parent entity, group names).
 */
export function collectOwnerSearchTokens(groups, stakeholders) {
	const tokens = [];

	const add = (raw) => {
		if (raw == null) return;
		const s = String(raw).trim();
		if (s) tokens.push(s.toLowerCase());
	};

	groups.forEach((g) => {
		add(g.name || g.groupName);
		getDataOwnerEmailsForGroup(g).forEach((e) => add(e));
		g?.branches?.forEach((b) => {
			addBranchEntitySearchTokens(b, add);
			(b.emails || []).filter(Boolean).forEach((e) => add(e));
		});
	});

	stakeholders.forEach((s) => {
		add(s.name);
		add(s.email);
	});

	return tokens;
}

/**
 * Lowercase blob of all rendered data-owner lines (matches UI, including "Parent: email" patterns).
 */
export function getOwnerDisplaySearchBlob(row) {
	if (!row?.blocks?.length) return '';
	return row.blocks
		.flatMap((b) => b.ownerLines || [])
		.map((s) => String(s).trim())
		.filter(Boolean)
		.join(' ')
		.toLowerCase();
}

export function formatApproverLinesForGroup(group, tApproverLevel) {
	const byLevel = getReviewersGroupedByLevel(group);
	if (byLevel.length === 0) return [];
	return byLevel.map(({ level, emails }) => ({
		level,
		emails: [...emails],
		label: tApproverLevel
			? `${tApproverLevel(level)}: ${emails.join(', ')}`
			: `Level ${level}: ${emails.join(', ')}`
	}));
}

/**
 * True if the row has at least one non-empty data owner line (group branches, stakeholder, or group fallback).
 */
export function questionRowHasDataOwners(row) {
	if (!row?.blocks?.length) return false;
	return row.blocks.some((b) =>
		(b.ownerLines || []).some((line) => String(line).trim().length > 0)
	);
}

/** Total number of question fields in the form preview (before owner filtering). */
export function countQuestionsInSkeleton(formSkeleton) {
	return collectQuestionRowsFromSkeleton(formSkeleton?.sections || []).length;
}

/**
 * Builds display-ready rows for the DCE question permissions table.
 * @param {object} formSkeleton - Response from GET /forms/{id}/preview
 * @param {object[]} formPermissions - formPermissions from GET /assessments/{id}
 * @param {(level: number) => string} [tApproverLevel] - i18n for level label
 */
export function buildDceQuestionPermissionRows(formSkeleton, formPermissions, tApproverLevel) {
	const skeletonRows = collectQuestionRowsFromSkeleton(formSkeleton?.sections);
	return skeletonRows.map((r) => {
		const { groups, stakeholders } = getApplicablePermissionTargets(
			formPermissions,
			r.ancestorIds
		);

		const groupBlocks = groups.map((g) => ({
			type: 'group',
			id: g.id,
			name: g.name || g.groupName || '',
			ownerLines: formatOwnerLinesForGroup(g),
			approverLines: formatApproverLinesForGroup(g, tApproverLevel)
		}));

		const stakeholderBlocks = stakeholders.map((s) => ({
			type: 'stakeholder',
			id: s.id,
			name: s.name || s.email || '',
			ownerLines: [s.name || s.email || ''].filter(Boolean),
			approverLines: []
		}));

		const ownerSearchTokens = collectOwnerSearchTokens(groups, stakeholders);

		return {
			fieldId: r.fieldId,
			sectionTitle: r.sectionTitle,
			subSectionTitles: r.subSectionTitles,
			questionName: r.questionName,
			ownerSearchTokens,
			blocks: [...groupBlocks, ...stakeholderBlocks]
		};
	}).filter(questionRowHasDataOwners);
}

/**
 * Returns true if the row matches the search string on data owners or on question hierarchy text.
 */
export function rowMatchesDceSearchQuery(row, query) {
	const q = (query || '').trim().toLowerCase();
	if (!q) return true;

	if (row.ownerSearchTokens?.some((t) => t.includes(q))) return true;

	const ownersDisplay = getOwnerDisplaySearchBlob(row);
	if (ownersDisplay.includes(q)) return true;

	const textParts = [
		row.sectionTitle,
		...(row.subSectionTitles || []),
		row.questionName
	]
		.filter(Boolean)
		.map((s) => String(s).toLowerCase());

	return textParts.some((p) => p.includes(q));
}

/**
 * Filters and sorts question rows for the search box (data owners, section, subsection, question text).
 * When `query` is empty, returns `rows` unchanged.
 */
export function filterRowsBySearchQuery(rows, query) {
	const q = (query || '').trim().toLowerCase();
	if (!q || !rows?.length) return rows || [];

	const hierarchyKey = (row) =>
		[
			row.sectionTitle || '',
			...(row.subSectionTitles || []),
			row.questionName || ''
		].join('\0');

	return rows
		.filter((row) => rowMatchesDceSearchQuery(row, q))
		.sort((a, b) => hierarchyKey(a).localeCompare(hierarchyKey(b), undefined, { sensitivity: 'base' }));
}

/** @deprecated Use {@link filterRowsBySearchQuery} */
export function filterRowsByDataOwnerQuery(rows, query) {
	return filterRowsBySearchQuery(rows, query);
}

/**
 * Groups flat question rows (already sorted) into sections and subsection buckets for table display.
 * @returns {Array<{ sectionTitle: string, subGroups: Array<{ subKey: string, subSectionTitles: string[], rows: object[] }> }>}
 */
export function buildNestedSectionGroups(rows) {
	if (!rows?.length) return [];

	const sections = [];
	const sectionIndex = new Map();

	for (const row of rows) {
		const s = row.sectionTitle || '';
		let si = sectionIndex.get(s);
		if (si === undefined) {
			si = sections.length;
			sectionIndex.set(s, si);
			sections.push({ sectionTitle: s, subGroups: [] });
		}
		const subKey = (row.subSectionTitles || []).join('/');
		const sec = sections[si];
		let sub = sec.subGroups.find((sg) => sg.subKey === subKey);
		if (!sub) {
			sub = {
				subKey,
				subSectionTitles: row.subSectionTitles ? [...row.subSectionTitles] : [],
				rows: []
			};
			sec.subGroups.push(sub);
		}
		sub.rows.push(row);
	}

	return sections;
}

/**
 * Plain-text data owners for Excel export — one line per owner entry (newlines for wrapping in Excel).
 */
export function formatDceRowOwnersForExport(row) {
	if (!row?.blocks?.length) return '';
	const parts = [];
	for (const b of row.blocks) {
		const lines = (b.ownerLines || []).map((s) => String(s).trim()).filter(Boolean);
		if (lines.length === 0) continue;
		if (b.type === 'group' && b.name) {
			const gname = String(b.name).trim();
			if (gname) parts.push(gname);
			lines.forEach((line) => parts.push(line));
		} else {
			lines.forEach((line) => parts.push(line));
		}
	}
	return parts.join('\n');
}

/**
 * Plain-text approvers for Excel export — level heading then one email per line (no group name).
 * @param {(level: number) => string} [tApproverLevel] - e.g. i18n "Level {{level}}"
 */
export function formatDceRowApproversForExport(row, tApproverLevel) {
	if (!row?.blocks?.length) return '';
	const blockChunks = [];
	for (const b of row.blocks) {
		if (!b.approverLines?.length) continue;
		const parts = [];
		for (const a of b.approverLines) {
			if (a.emails?.length > 0) {
				const lvl = a.level ?? 1;
				const levelLabel = tApproverLevel
					? String(tApproverLevel(lvl)).trim()
					: `Level ${lvl}`;
				parts.push(levelLabel);
				for (const email of a.emails) {
					const e = String(email).trim();
					if (e) parts.push(e);
				}
			} else if (a.label) {
				parts.push(String(a.label).trim());
			}
		}
		while (parts.length && parts[parts.length - 1] === '') parts.pop();
		if (parts.length) blockChunks.push(parts.join('\n'));
	}
	return blockChunks.join('\n\n');
}

/**
 * Builds a 2D array for legacy xlsx / tests (header row + one row per question).
 * @param {string[]} headerRow - five translated column titles [section, subsection, question, owners, approvers]
 * @param {(level: number) => string} [tApproverLevel] - passed to {@link formatDceRowApproversForExport}
 */
export function buildDcePermissionsExportAoA(rows, headerRow, tApproverLevel) {
	const data = (rows || []).map((row) => [
		row.sectionTitle || '',
		(row.subSectionTitles || []).join(' › '),
		row.questionName || '',
		formatDceRowOwnersForExport(row),
		formatDceRowApproversForExport(row, tApproverLevel)
	]);
	return [headerRow, ...data];
}
